import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { Helmet } from 'react-helmet-async';

interface ProfileData {
    name: string;
    email: string;
}

interface PasswordData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ProfileSettings: React.FC = () => {
    const [user, loading, error] = useAuthState(auth);
    const [profileData, setProfileData] = useState<ProfileData>({
        name: '',
        email: ''
    });
    const [passwordData, setPasswordData] = useState<PasswordData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

    // Initialize profile data when user loads
    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.displayName || '',
                email: user.email || ''
            });
        }
    }, [user]);

    const updateOwnProfileFn = httpsCallable(functions, 'updateOwnProfile');
    const changeOwnPasswordFn = httpsCallable(functions, 'changeOwnPassword');

    const handleProfileUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setProfileLoading(true);
        setProfileError(null);
        setProfileSuccess(null);

        try {
            await updateOwnProfileFn({ name: profileData.name });
            setProfileSuccess('Profil uppdaterad framgångsrikt!');
            
            // Update the user's display name in the auth state
            if (user) {
                await user.reload();
            }
        } catch (err: any) {
            setProfileError(err.message || 'Ett fel uppstod vid uppdatering av profil');
        } finally {
            setProfileLoading(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordLoading(true);
        setPasswordError(null);
        setPasswordSuccess(null);

        // Client-side validation
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('Nya lösenordet matchar inte bekräftelsen');
            setPasswordLoading(false);
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordError('Nya lösenordet måste vara minst 6 tecken långt');
            setPasswordLoading(false);
            return;
        }

        if (!user || !user.email) {
            setPasswordError('Användarinformation saknas');
            setPasswordLoading(false);
            return;
        }

        try {
            // Re-authenticate user with current password
            const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password using Firebase Auth
            await updatePassword(user, passwordData.newPassword);

            setPasswordSuccess('Lösenord ändrat framgångsrikt!');
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (err: any) {
            console.error('Password change error:', err);
            if (err.code === 'auth/wrong-password') {
                setPasswordError('Nuvarande lösenord är felaktigt');
            } else if (err.code === 'auth/weak-password') {
                setPasswordError('Nya lösenordet är för svagt');
            } else {
                setPasswordError(err.message || 'Ett fel uppstod vid ändring av lösenord');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <Helmet>
                    <title>Profilinställningar - Molnfastigheter</title>
                </Helmet>
                <div className="text-center">
                    <Spinner animation="border" />
                    <p className="mt-2">Laddar profil...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Helmet>
                    <title>Profilinställningar - Molnfastigheter</title>
                </Helmet>
                <Alert variant="danger">
                    <Alert.Heading>Fel vid laddning av profil</Alert.Heading>
                    <p>{error.message}</p>
                </Alert>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container className="mt-4">
                <Helmet>
                    <title>Profilinställningar - Molnfastigheter</title>
                </Helmet>
                <Alert variant="warning">
                    <Alert.Heading>Inte inloggad</Alert.Heading>
                    <p>Du måste vara inloggad för att komma åt denna sida.</p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <Helmet>
                <title>Profilinställningar - Molnfastigheter</title>
            </Helmet>
            <Row>
                <Col>
                    <h2>Profilinställningar</h2>
                    <p className="text-muted">Hantera din profil och säkerhetsinställningar</p>
                </Col>
            </Row>

            <Row className="mt-4">
                {/* Profile Information */}
                <Col lg={6} className="mb-4">
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Profilinformation</h5>
                        </Card.Header>
                        <Card.Body>
                            {profileError && (
                                <Alert variant="danger" dismissible onClose={() => setProfileError(null)}>
                                    {profileError}
                                </Alert>
                            )}
                            {profileSuccess && (
                                <Alert variant="success" dismissible onClose={() => setProfileSuccess(null)}>
                                    {profileSuccess}
                                </Alert>
                            )}
                            
                            <Form onSubmit={handleProfileUpdate}>
                                <Form.Group className="mb-3">
                                    <Form.Label>E-postadress</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={profileData.email}
                                        disabled
                                        className="bg-light"
                                    />
                                    <Form.Text className="text-muted">
                                        E-postadressen kan inte ändras
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Namn</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={profileData.name}
                                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                        placeholder="Ange ditt namn"
                                        required
                                    />
                                </Form.Group>

                                <Button 
                                    variant="primary" 
                                    type="submit" 
                                    disabled={profileLoading || !profileData.name.trim()}
                                >
                                    {profileLoading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Uppdaterar...
                                        </>
                                    ) : (
                                        'Uppdatera profil'
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Password Change */}
                <Col lg={6} className="mb-4">
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Ändra lösenord</h5>
                        </Card.Header>
                        <Card.Body>
                            {passwordError && (
                                <Alert variant="danger" dismissible onClose={() => setPasswordError(null)}>
                                    {passwordError}
                                </Alert>
                            )}
                            {passwordSuccess && (
                                <Alert variant="success" dismissible onClose={() => setPasswordSuccess(null)}>
                                    {passwordSuccess}
                                </Alert>
                            )}
                            
                            <Form onSubmit={handlePasswordChange}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nuvarande lösenord</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={passwordData.currentPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                        placeholder="Ange nuvarande lösenord"
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Nytt lösenord</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                        placeholder="Ange nytt lösenord"
                                        required
                                        minLength={6}
                                    />
                                    <Form.Text className="text-muted">
                                        Minst 6 tecken
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Bekräfta nytt lösenord</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={passwordData.confirmPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                        placeholder="Bekräfta nytt lösenord"
                                        required
                                        minLength={6}
                                    />
                                </Form.Group>

                                <Button 
                                    variant="warning" 
                                    type="submit" 
                                    disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                                >
                                    {passwordLoading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Ändrar...
                                        </>
                                    ) : (
                                        'Ändra lösenord'
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* User Information */}
            <Row className="mt-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Kontoinformation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <p><strong>Användar-ID:</strong> {user.uid}</p>
                                    <p><strong>E-post verifierad:</strong> {user.emailVerified ? 'Ja' : 'Nej'}</p>
                                </Col>
                                <Col md={6}>
                                    <p><strong>Konto skapat:</strong> {user.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString('sv-SE') : 'Okänt'}</p>
                                    <p><strong>Senast inloggad:</strong> {user.metadata.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString('sv-SE') : 'Okänt'}</p>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ProfileSettings;
